import moment from "moment";
import _, { compact, find, isEmpty, isString, map, omitBy } from "lodash";
import { applyDateFilter } from "hooks/useFindingFiltersByGlobalFilters";
import { CellRenderers } from "./tableUtils";
import { FUTURE_OPTIONS, PAST_OPTIONS, parseDueDateUIFilter } from "components/ColumnFilterDueDate";
import { convertRelativeToAbsolute } from "ac-components/ACDateRangeFilter";
import { DATE_FILTER_API_SUPPORT } from "views/Findings/constants/columns";
// eslint-disable-next-line
const TDATE = new Date().toTimeString().match(/\(([^\)]+)\)/)[1].match(/([A-Z])/g);

export function setStartOfTheDay(time){
    if(!moment.isMoment(time)){
        time = moment(time);
    }
    return time.startOf("D");
}

export function setEndOfTheDay(time){
    if(!moment.isMoment(time)){
        time = moment(time);
    }
    return time.endOf("D");
}

export function getStartAndEndDateForQuickOption({ start = 0, end = 0, granularity = "days", label, utcString = false }) {
    if(label){
        const option = _.find(DateRangeQuickOptions, { label });
        if(option){
            ({ start = 0, end = 0, granularity } = option);
        }
    }
    if(!start && !end){
        return [];
    }
    const startDate = moment();
    const endDate = moment();
    if(granularity !== "hour"){
        startDate.startOf("D");
        endDate.endOf("D");
    }
    startDate.add(-start, granularity);
    endDate.add(-end, granularity);
    if(utcString){
        return [startDate.utc().format(), endDate.utc().format()];
    }
    return [startDate, endDate];
}

export const DateRangeQuickOptions = [{
    label: "All data"
}, {
    label: "Last 24 hour",
    key: "LAST_24_HOUR",
    start: 24,
    granularity: "hour"
},
{
    label: "Last 48 hour",
    key: "LAST_48_HOUR",
    start: 48,
    granularity: "hour"
},
 {
    label: "Last 7 days",
    key: "LAST_7_DAYS",
    start: 7,
    granularity: "days"
}, {
    label: "Last 30 days",
    key: "LAST_1_MONTH",
    start: 30,
    granularity: "days"
}, {
    label: "Last 90 days",
    key: "LAST_3_MONTHS",
    start: 90,
    granularity: "days"
}, {
    label: "Last 180 days",
    key: "LAST_6_MONTHS",
    start: 180,
    granularity: "days"
}, {
    label: "Last month",
    isHidden: true,
    key: "LAST_1_MONTH",
    start: 30,
    granularity: "days"
}, {
    label: "Last 3 months",
    isHidden: true,
    key: "LAST_3_MONTHS",
    start: 90,
    granularity: "days"
}, {
    label: "Last 6 months",
    isHidden: true,
    key: "LAST_6_MONTHS",
    start: 180,
    granularity: "days"
}]

export function getFindingAPIDurationFilter(filters = {}, selectedDuration, startDate, endDate){
    filters = { ...filters };
    if(!_.isEmpty(selectedDuration)){
        filters.foundOn = getStartAndEndDateForQuickOption({ label: selectedDuration, utcString: true });
    } else if(startDate && endDate){
        filters.foundOn = [startDate, endDate];
    }
    return filters;
}

export function populateNonExistentDate(startDate, list, selectedDuration, aggregateBy, endDate){
    const dataAvailableFromDate = moment(list[0]?.dateRaw);
    const dataAvailableToDate = moment(list[list.length - 1]?.dateRaw);

    const _startDate = startDate || (selectedDuration && getStartAndEndDateForQuickOption({ label: selectedDuration, utcString: true })[0]);
    const _endDate = endDate || (selectedDuration && getStartAndEndDateForQuickOption({ label: selectedDuration, utcString: true })[1]);

    const _startDateMoment = moment(_startDate);
    const _endDateMoment = moment(_endDate);

    const aggregateByMonth = aggregateBy === 'Month';
    const aggregateByWeek = aggregateBy === 'Week';

    if (!_.isUndefined(_startDate) && dataAvailableFromDate.isAfter(_startDateMoment, 'day')) {
        let current = dataAvailableFromDate;
        while (_startDateMoment.isBefore(current, 'day')) {
            current = current.subtract(aggregateByWeek ? 7 : 1, aggregateByMonth ? 'months' : 'd');
            list.unshift({
              dateRaw: current.toDate().valueOf(),
              Critical: 0,
              High: 0,
              Low: 0,
              Medium: 0,
              Mitigated: 0,
              Total: 0,
              reported: 0,
              pendingCount: 0,
              date: current.format(aggregateByMonth ? "MMM YYYY" : "DD MMM"),
            });

        }
    }

    if (!_.isUndefined(_endDate) && dataAvailableToDate.isBefore(_endDateMoment, 'day')) {
        let current = dataAvailableToDate;
        while (_endDateMoment.isAfter(current, 'day')) {
            current = current.add(aggregateByWeek ? 7 : 1, aggregateByMonth ? 'months' : 'd');
            list.push({
                dateRaw: current.toDate().getMilliseconds(), 
                Critical: 0,
                High: 0,
                Low: 0,
                Medium: 0,
                Mitigated: 0,
                Total: 0,
                date: current.format(aggregateByMonth ? "MMM YYYY" : "DD MMM"),
            });

        }
    }
}

export function getCurrentYear() {
    const year = moment().year();
    return year;
}

export const checkForAggregationByMonth = (startDate, endDate, selectedDuration) => {
    if (!startDate && !endDate && !selectedDuration) {
        return true;
    }

    let duration = [startDate, endDate];
    if (selectedDuration) {
        duration = getStartAndEndDateForQuickOption({label: selectedDuration, utcString: true });
    }

    const [start, end] = duration;
    return moment(end).diff(moment(start), 'days') > 60;
}

export const getFormattedStartAndEndDate = (startDate, endDate) => ({
    startDate: moment(startDate).format("ddd, DD MMM YYYY HH:mm:ss [GMT]"),
    endDate: moment(endDate).format("ddd, DD MMM YYYY HH:mm:ss [GMT]"),
})

export function getDateLabelByInterval(datetime, interval){
    return moment(datetime).format(
        interval === "Month"
            ? "MMM YYYY"
            : interval === "Day"
            ? "DD MMM"
            : "[wk] DD MMM"
    );
}

export function fillEmptyStartDates({ startDate, data, aggregateBy, keyName }) {
    const aggregateByWeek = aggregateBy === "Week";
    const aggregateByMonth = aggregateBy === "Month";
    const startDateMoment = moment(startDate);
    const dataAvailableFromDate = moment(data[0].date);
    if (
        !_.isUndefined(startDate) &&
        dataAvailableFromDate.isAfter(startDateMoment, "day")
    ) {
        let current = dataAvailableFromDate;
        while (startDateMoment.isBefore(current, "day")) {
            current = current.subtract(
                aggregateByWeek ? 7 : 1,
                aggregateByMonth ? "months" : "d"
            );
            data.unshift({
                date: current.toDate().valueOf(),
                dateText: moment(current.toDate().valueOf()).format(
                aggregateBy === "Month"
                    ? "MMM YYYY"
                    : aggregateBy === "Day"
                    ? "DD MMM"
                    : "[wk] DD MMM"
                ),
                [keyName]: 0
            });
        }
    }
}

export function validateDurationByInterval(duration, interval) {
    let error = null;
    if (interval && duration) {
        const [startDate, endDate] =
            duration.length === 1
                ? getStartAndEndDateForQuickOption({
                    label: duration[0],
                    utcString: true,
                })
                : duration;
        const difference = moment(endDate).diff(moment(startDate), "days");

        if (interval === "Week" && difference < 7) {
            error = "Date range should be greater than or equal to 7 days.";
        } else if (interval === "Month" && difference < 30) {
            error = "Date range should be greater than or equal to 30 days.";
        } else {
            error = null;
        }
    }

    if (error) {
        return Promise.reject(error);
    }
    return Promise.resolve();
}

export function getEffectDateRangeForTimelineCharts(
    widgetRange,
    selectedDurationFromUrl,
    rangeFromUrl,
    plainUtc
) {
    const dateFilters = omitBy({ foundOn: compact(widgetRange) }, isEmpty);
    applyDateFilter(dateFilters, selectedDurationFromUrl, rangeFromUrl);
    if (!isEmpty(dateFilters.foundOn)) {
        return plainUtc
          ? map(dateFilters.foundOn, (d) => new Date(d).toUTCString())
          : dateFilters.foundOn;
    }
    return [];
}

export function validateIntervalAndDuration(interval, duration){
    let error = null;
    if (interval && duration?.length > 0) {
        const [startDate, endDate] = convertDateFilterValueToDate(undefined, duration);
        const difference = moment(endDate).diff(
            moment(startDate),
            "days"
        );

        if (interval === "Week" && difference < 7) {
            error =
                "Duration should be greater than or equal to 7 days.";
        } else if (interval === "Month" && difference < 30) {
            error =
                "Duration should be greater than or equal to 30 days.";
        } else {
            error = null;
        }
    }

    if (error) {
        throw new Error(error);
    }
}

export const RelativeTimeOptions = [
  { value: "h", label: "Hours ago" },
  { value: "d", label: "Days ago" },
  { value: "w", label: "Weeks ago" },
  { value: "M", label: "Months ago" },
  { value: "y", label: "Years ago" },
  { value: "h+", label: "Hours from now" },
  { value: "d+", label: "Days from now" },
  { value: "w+", label: "Weeks from now" },
  { value: "M+", label: "Months from now" },
  { value: "y+", label: "Years from now" },
];

export const parseDateToDisplay = (value) => {
    if(!value){
        return value;
    }
    if (!isNaN(new Date(value).getTime())) {
      return CellRenderers.DATE(value);
    }
    const relativeOption = find(RelativeTimeOptions, (option) =>
      value.endsWith(` ${option.value}`)
    );
    if(relativeOption){
        return `${value.replace(relativeOption.value, relativeOption.label)}`;
    }
    if(value === "now"){
        return "Now";
    }
    return value;
}

export const getDateTypeFromValue = (value) => {
    if (!value) {
        return "relative";
    }
    if (!isNaN(new Date(value).getTime())) {
      return "absolute";
    }
    const relativeOption = find(RelativeTimeOptions, (option) =>
      value.endsWith(` ${option.value}`)
    );
    if(relativeOption){
        return "relative";
    }
    if(value === "now"){
        return value;
    }
    return undefined;
}

export const parseOldDateFormatsToDate = (filterKey, values) => {
    const dueDateKeys = ["slaDueDate", "triageDueDate", "remediationDueDate", "dueDate"];
    if(dueDateKeys.includes(filterKey)){
        if(values.length === 2){
            // ["10", ""] - 10 days ago till now
            // ["", "10"] - now till next 10 day
            // ["10", "10"] - 10 days ago till next 10 days
            if((values[0] <= 1000 || values[0] === "") && (values[1] <= 1000 || values[1] === "")){
                return parseDueDateUIFilter(values);
            }
        } else if(values.length === 1){
            return parseDueDateUIFilter(values);
        }
    } else {
        if(values.length === 1){
            return getStartAndEndDateForQuickOption({ label: values[0], utcString: true });
        }
    }
}

export function disablePastDates(current){
    return current && current.isBefore(moment(), 'day');
}

export function disableCurrentAndPastDates(current){
    return current && (current.isBefore(moment(), 'day') || current.isSame(moment(), 'day'));
}

export const parseOldDateFormatsToNew = (filterKey, values) => {
    const dueDateKeys = ["slaDueDate", "triageDueDate", "remediationDueDate", "dueDate"];
    if(dueDateKeys.includes(filterKey)){
        if(values.length === 2){
            // ["10", ""] - 10 days ago till now
            // ["", "10"] - now till next 10 day
            // ["10", "10"] - 10 days ago till next 10 days
            if((values[0] <= 1000 || values[0] === "") && (values[1] <= 1000 || values[1] === "")){
                return [values[0] === "" ? "now" : values[0] + " d", values[1] === "" ? "now" : values[1] + " d+"];
            }
        } else if(values.length === 1){
            let object = _.find(PAST_OPTIONS, { value: values[0] });
            if(object){
                const { startOffset, endOffset } = object;
                return [
                  `${Math.abs(startOffset)} d`,
                  endOffset === 0 ? "now" : `${Math.abs(endOffset)} d`,
                ];
            } else {
                object = _.find(FUTURE_OPTIONS, { value: values[0] });
                if (object) {
                    const { endOffset } = object;
                    return [
                      `now`, // all startOffset in future options are 0
                      endOffset === 0 ? "now" : `${Math.abs(endOffset)} d+`, // all end offset are either 0 or future
                    ];
                }
            }
        }
    } else {
        if(values.length === 1){
            const option = _.find(DateRangeQuickOptions, { label: values[0] });
            if(option){
                const { start = 0, granularity } = option;
                return [`${start} ${granularity[0]}`, "now"];
            }
        }
    }
}

export const convertDateFilterValueToDate = (key, values) => {
    if(isEmpty(values)) {
        return [];
    }
    const oldToNew = parseOldDateFormatsToNew(key, values);
    if(!isEmpty(oldToNew)){
        values = oldToNew;
    }
    return map(values, (value, i) => {
        if(_.isNil(value)) {
            return null;
        }
        let epoch = value;
        if(value === "now"){
            epoch = Date.now();
        } else if(isString(value) && !moment(value).isValid()){
            epoch = convertRelativeToAbsolute(value, i === 0 ? "start" : "end");
        }
        return moment(epoch).utc().format();
    });
}

export const getOldFormatToNewDateFilters = (filters, dateFilters = DATE_FILTER_API_SUPPORT) => {
    dateFilters.forEach((key) => {
      if (!isEmpty(filters[key])) {
        const updatedValues = parseOldDateFormatsToNew(key, filters[key]);
        if (!isEmpty(updatedValues)) {
          filters[key] = updatedValues;
        }
      }
    });
  
    return filters;
};
